import hoverintent from 'hoverintent';
import { initiateFadeInUpAnimation, removeFadeInUpAnimation } from './fadeInUpAnimation';

export default class Menu {
  constructor(theme, element) {
    this.theme = theme;
    this.element = element;
    this.activeClass = 'nav-hover';
    this.sectionId = this.element.getAttribute('data-wetheme-section-id');
    this.megaMenuDropdowns = document.querySelectorAll('.site-nav__dropdown.mega-menu-dropdown');
    this.megaMenu = document.querySelector('.mega-menu-block');
    this.megaMenuChildren = document.querySelectorAll('.mega-menu-block .dropdown-submenu');
    this.resizeTimeout = null;
    this.hideTimeout = null;
    this.megaMenuHideTimeout = null;

    this.nav = document.getElementById('AccessibleNav');
    this.allLinks = this.nav.querySelectorAll('a');
    this.topLevel = this.nav.querySelectorAll('li > a');
    this.parents = this.nav.querySelectorAll('.site-nav--has-dropdown');
    this.mainLinks = this.nav.querySelectorAll('.site-nav--has-dropdown.first-level-dropdown');
    this.subMenus = this.nav.querySelectorAll('.site-nav__dropdown');
    this.subMenuLinks = this.nav.querySelectorAll('.site-nav__dropdown a');
    this.overlayHeader = document.querySelector('header.overlay-header');

    this.parents.forEach((el) => {
      this.setDropdownOffset(el);
    });

    this.subMenus.forEach((el) => {
      this.initScrollHandler(el);
      if (el.classList.contains('mega-menu-dropdown')) {
        // Delay the hide of the mega menu dropdown
        el.addEventListener('mouseleave', (e) => {
          if (window.innerWidth >= 1024) {
            // Check if section is selected in theme editor
            if (this.element.classList.contains('section-selected')) {
              return;
            }

            // Check if mouse is moving back to the mega menu trigger
            const trigger = el.closest('.site-nav--has-dropdown.mega-menu');

            // If relatedTarget is the trigger or a child of the trigger, don't hide
            if (trigger && (e.relatedTarget === trigger || trigger.contains(e.relatedTarget))) {
              return;
            }

            this.megaMenuHideTimeout = setTimeout(() => {
              window.eventBus.emit('mega-menu:hide');
            }, 150);
          } else {
            this.scheduleHideDropdown(el);
          }
        });

        el.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 1024) {
            if (this.megaMenuHideTimeout) {
              clearTimeout(this.megaMenuHideTimeout);
              this.megaMenuHideTimeout = null;
            }
          }
        });

        el.addEventListener('focusout', () => this.scheduleHideDropdown(el));

        // Close on link click
        el.querySelectorAll('a').forEach(link => {
          link.addEventListener('click', () => {
            if (window.innerWidth >= 1024) {
              window.eventBus.emit('mega-menu:hide');
            }
          });
        });

      } else {
        // Close the dropdown immediately
        el.addEventListener('mouseleave', () => this.hideDropdown(el));
        el.addEventListener('focusout', () => this.hideDropdown(el));
      }

      // Add mouseover event to handle child dropdowns
      el.querySelectorAll('li').forEach((childEl) => {
        childEl.addEventListener('mouseover', (evt) => this.handleChildHover(evt, childEl));
      });
    });

    this.adjustMegaMenuPosition();

    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.parents.forEach((el) => {
          this.setDropdownOffset(el);
          this.adjustMegaMenuPosition();
        });
      }, 300);
    });

    // EventBus listeners
    window.eventBus.on('mega-menu:show', (data) => {
      const { triggerId, dropdownId } = data;
      const dropdown = document.getElementById(dropdownId);
      const trigger = document.querySelector(`[data-mega-menu-trigger-id="${dropdownId}"]`);

      if (dropdown) {
        // Hide other mega menus
        this.megaMenuDropdowns.forEach(d => {
          if (d.id !== dropdownId) {
            d.classList.remove('show');
            d.classList.remove(this.activeClass);
          }
        });

        // Show this one
        dropdown.classList.add('show');
        dropdown.classList.add(this.activeClass);
        
        // Handle overlay header
        if (this.overlayHeader) {
          this.overlayHeader.classList.add('force-hover');
        }

        // Animation
        const megaMenuChildrenArray = Array.from(dropdown.querySelectorAll('.mega-menu-block .dropdown-submenu'));
        initiateFadeInUpAnimation(megaMenuChildrenArray);
      }
    });

    window.eventBus.on('mega-menu:hide', () => {
      this.megaMenuDropdowns.forEach(d => {
        d.classList.remove('show');
        d.classList.remove(this.activeClass);
        
        const megaMenuChildrenArray = Array.from(d.querySelectorAll('.mega-menu-block .dropdown-submenu'));
        removeFadeInUpAnimation(megaMenuChildrenArray);
      });

      if (this.overlayHeader) {
        this.overlayHeader.classList.remove('force-hover');
      }
    });

    if (window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.element.classList.add('section-selected');
        } else {
          this.element.classList.remove('section-selected');
        }
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.element.classList.remove('section-selected');
        }
      });

      document.addEventListener('shopify:section:load', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.adjustMegaMenuPosition();
        }
      });

      document.addEventListener('shopify:block:select', (e) => {
        // Find the mega menu block that was selected
        const blockElement = document.querySelector(`[data-mega-menu-block-id="${e.detail.blockId}"]`);
        if (blockElement) {
          // Find the dropdown that contains this block
          const dropdown = blockElement.closest('.site-nav__dropdown.mega-menu-dropdown');
          if (dropdown) {
            const dropdownId = dropdown.id;
            const trigger = document.querySelector(`[data-mega-menu-trigger-id="${dropdownId}"]`);
            
            if (trigger) {
              // Mark section as selected to prevent mouseleave from hiding
              this.element.classList.add('section-selected');
              
              // Show the mega menu
              window.eventBus.emit('mega-menu:show', { dropdownId });
            }
          }
        }
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        // Find the mega menu block that was deselected
        const blockElement = document.querySelector(`[data-mega-menu-block-id="${e.detail.blockId}"]`);
        if (blockElement) {
          // Find the dropdown that contains this block
          const dropdown = blockElement.closest('.site-nav__dropdown.mega-menu-dropdown');
          if (dropdown) {
            // Remove section-selected class
            this.element.classList.remove('section-selected');
            
            // Hide the mega menu
            window.eventBus.emit('mega-menu:hide');
          }
        }
      });
    }

    this.parents.forEach((el) => {
      el.addEventListener('touchstart', (evt) => this.handleParentEvent(evt, el));
      el.addEventListener('focusin', (evt) => this.handleParentEvent(evt, el));
      
      // Mega Menu Trigger Logic
      if (el.classList.contains('mega-menu')) {
        el.addEventListener('mouseleave', (e) => {
          if (window.innerWidth >= 1024) {
            // Check if section is selected in theme editor
            if (this.element.classList.contains('section-selected')) {
              return;
            }

            // Check if mouse is moving towards the mega menu dropdown
            const dropdownId = el.getAttribute('data-mega-menu-trigger-id');
            const dropdown = dropdownId ? document.getElementById(dropdownId) : null;

            // If relatedTarget is the dropdown or a child of the dropdown, don't hide
            if (dropdown && (e.relatedTarget === dropdown || dropdown.contains(e.relatedTarget))) {
              return;
            }

            this.megaMenuHideTimeout = setTimeout(() => {
              window.eventBus.emit('mega-menu:hide');
            }, 150);
          } else {
            this.scheduleHideDropdown(el);
          }
        });
      } else {
        // For non-mega menu items, hide any open mega menu
        // el.addEventListener('mouseenter', () => {
        //   if (window.innerWidth >= 1024) {
        //     if (this.megaMenuHideTimeout) {
        //       clearTimeout(this.megaMenuHideTimeout);
        //       this.megaMenuHideTimeout = null;
        //     }
        //     window.eventBus.emit('mega-menu:hide');
        //   }
        // });
      }
    });

    this.parents.forEach((el) => {
      hoverintent(
        el,
        (evt) => {
          // Show the dropdown (standard or mega)
          this.handleParentEvent(evt, el);
        },
        () => {
          // Avoid using hideDropdown() here.
        }
      );
    });

    this.mainLinks.forEach((el) => {
      if (el.classList.contains('mega-menu')) {
        // Delay the hide of the mega menu dropdown
        // el.addEventListener('mouseleave', () => this.scheduleHideDropdown(el)); // Handled above
      } else {
        // Close the dropdown immediately
        el.addEventListener('mouseleave', () => this.hideDropdown(el));
      }
    });

    document.querySelector('.site-header').addEventListener('mouseleave', () => {
      // Replace direct hideDropdown calls with scheduleHideDropdown
      this.scheduleHideDropdown();
    });

    this.subMenus.forEach((el) => {
      if (el.classList.contains('mega-menu-dropdown')) {
        // Delay the hide of the mega menu dropdown
        el.addEventListener('mouseleave', () => this.scheduleHideDropdown(el));
        el.addEventListener('focusout', () => this.scheduleHideDropdown(el));
      } else {
        // Close the dropdown immediately
        el.addEventListener('mouseleave', () => this.hideDropdown(el));
        el.addEventListener('focusout', () => this.hideDropdown(el));
      }
    });

    if (this.megaMenu) {
      // Replace direct hideDropdown calls with scheduleHideDropdown
      this.megaMenu.addEventListener('mouseleave', () => this.scheduleHideDropdown());
    }

    this.subMenuLinks.forEach((link) => {
      link.addEventListener('touchstart', (evt) => {
        evt.stopImmediatePropagation();
      });
    });

    this.allLinks.forEach((link) => {
      link.addEventListener('focusin', (evt) => {
        const parentEl = link.parentElement;
        this.closeSiblings(parentEl, evt);
      });
    });

    document.querySelectorAll('.site-header a:not(.swym-wishlist) .site-header a, .site-header button, .site-header select').forEach((el) => {
      el.addEventListener('focusin', () => {
        let closeAllMenus = true;
        this.allLinks.forEach((link) => {
          if (link === el) {
            closeAllMenus = false;
          }
        });

        if (closeAllMenus) {
          this.parents.forEach((parent) => {
            this.hideDropdown(parent);
          });
        }
      });
    });

    // Bind the event handler
    this.handleBodyTouchStart = this.handleBodyTouchStart.bind(this);
  }

  // Named event handler for touchstart
  handleBodyTouchStart(evt) {
    if (!evt.target.closest('.site-nav__link, .dropdown-submenu')) {
      this.hideDropdown();
    }
  }

  handleChildHover(evt, childEl) {
    // Close all sibling dropdowns
    const siblings = Array.from(childEl.parentElement.children).filter(sibling => sibling !== childEl);
    this.hideDropdown(siblings);

    // Show the current dropdown if it has one
    if (childEl.classList.contains('site-nav--has-dropdown')) {
      this.showDropdown(childEl);
    }
  }

  handleParentEvent(evt, el) {
    let ret = true;
    if (!el.classList.contains(this.activeClass)) {
      evt.preventDefault();
      const siblings = Array.from(el.parentElement.children).filter(sibling => sibling !== el);
      this.hideDropdown(siblings);
      ret = false;
    }
    this.showDropdown(el);

    if (el.classList.contains('mega-menu') && window.innerWidth >= 1024) {
      if (this.megaMenuHideTimeout) {
        clearTimeout(this.megaMenuHideTimeout);
        this.megaMenuHideTimeout = null;
      }

      const dropdownId = el.getAttribute('data-mega-menu-trigger-id');
      if (dropdownId) {
        window.eventBus.emit('mega-menu:show', { dropdownId });
      }
    }

    return ret;
  }

  initScrollHandler(el) {
    let scrolling;
    el.addEventListener('scroll', () => {
      clearTimeout(scrolling);
      el.classList.add('scrolling');
      scrolling = setTimeout(() => {
        el.classList.remove('scrolling');
      }, 800);
    });
  }

  closeSiblings(el, evt) {
    if (!el.classList.contains(this.activeClass)) {
      evt.preventDefault();
      this.hideDropdown(Array.from(el.parentElement.children).filter(sibling => sibling !== el));
    }
  }

  adjustMegaMenuPosition() {
    // Need the scrollbar width in order to set the menu width to 100vw
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    this.element.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
  }

  showDropdown(el) {
    // Clear any existing hide timeout to prevent accidental hiding
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (!el) return;
    el.classList.add(this.activeClass);
    const megaMenuChildrenArray = Array.from(el.querySelectorAll('.mega-menu-block .dropdown-submenu'));
    initiateFadeInUpAnimation(megaMenuChildrenArray);

    document.querySelector('.sticky-header')?.classList.remove('sticky-header-transformed');

    if (el.classList.contains('mega-menu')) {
      // Add force-hover class to the overlay header when the mega menu dropdown is hovered
      if (this.overlayHeader) {
        this.overlayHeader.classList.add('force-hover');
      }
    } else {
      // Remove force-hover class from the overlay header if it exists
      if (this.overlayHeader && this.overlayHeader.classList.contains('force-hover')) {
        this.overlayHeader.classList.remove('force-hover');
      }
    }

    setTimeout(() => {
      document.body.addEventListener('touchstart', this.handleBodyTouchStart);
    }, 250);
  }

  /**
   * Schedule hiding the dropdown with a delay.
   * @param {HTMLElement} el - The dropdown element to hide.
  */
  scheduleHideDropdown(el) {
    // Clear any existing hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    // Set a new timeout to hide the dropdown after 300ms
    this.hideTimeout = setTimeout(() => {
      // Remove force-hover class from the overlay header when the mega menu dropdown is hidden
      if (this.overlayHeader) {
        this.overlayHeader.classList.remove('force-hover');
      }

      this.hideDropdown(el);
      this.hideTimeout = null;
    }, 500); // Delay in milliseconds
  }

  hideDropdown(elements) {
    // Clear any existing hide timeout to avoid multiple executions
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (elements) {
      // Ensure elements is an array or NodeList
      if (!Array.isArray(elements) && !(elements instanceof NodeList)) {
        elements = [elements]; // Convert single element to array
      }
  
      elements.forEach(el => {
        el.classList.remove(this.activeClass);
        const megaMenuChildrenArray = Array.from(el.querySelectorAll('.mega-menu-block .dropdown-submenu'));
        if (!this.element.classList.contains('section-selected') && !Array.from(this.parents).some(parent => parent.classList.contains('nav-hover'))) {
          removeFadeInUpAnimation(megaMenuChildrenArray);
        }
      });
    } else {
      document.querySelectorAll('.site-nav--has-dropdown').forEach((dropdown) => {
        dropdown.classList.remove(this.activeClass);
      });
  
      if (!this.element.classList.contains('section-selected') && !Array.from(this.parents).some(parent => parent.classList.contains('nav-hover'))) {
        removeFadeInUpAnimation(this.megaMenuChildren);
      }
    }
  
    document.body.removeEventListener('touchstart', this.handleBodyTouchStart);
  
    if (!document.querySelector('.nav-hover')) {
      const stickyHeader = document.querySelector('.sticky-header');
      if (stickyHeader) {
        stickyHeader.classList.add('sticky-header-transformed');
      }
    }
  }

  setDropdownOffset(el) {
    const dropdown = el.querySelector('.site-nav__dropdown.standard-dropdown');
    if (!dropdown) return;

    const navPosition = dropdown.getAttribute('data-logo-placement');
    const dropdownRight = window.innerWidth - (dropdown.getBoundingClientRect().left + dropdown.offsetWidth);

    if (dropdownRight < 15) {
      dropdown.style.right = '0';
      dropdown.style.left = 'auto';
    }

    // Get all subdropdown elements and apply the same logic to each one.
    const subDropdowns = dropdown.querySelectorAll('.site-nav__subdropdown');
    subDropdowns.forEach(subDropdown => {
      const subDropdownRight = window.innerWidth - (subDropdown.getBoundingClientRect().left + subDropdown.offsetWidth);
      if (subDropdownRight < 15) {
        if (navPosition === 'left' || navPosition === 'above') {
          subDropdown.style.left = 'auto';
          subDropdown.style.right = '100%';
        } else {
          subDropdown.style.left = '100%';
          subDropdown.style.right = 'auto';
        }
      }
    });
  }
}
